import type { OauthState } from "@/types/oauth.ts";
import { ssoOrganizationsQueryOptions } from "@/services/sso-service.ts";
import { readSsoFlowSecret } from "@/utils/oauth-flow-secret.ts";
import {
	connectReturnTarget,
	parseOauthState,
	peekSpaceConnectPayload,
	peekSsoOauthState,
	serializeOauthState,
} from "@/utils/oauth-state.ts";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { useOauthSso } from "../../components/auth/oauth/useOauthSso.ts";
import { OauthOrganizations } from "../../components/auth/oauth/oauth-organizations.tsx";

const oauthSearchSchema = z.object({
	code: z.string().optional(),
	state: z.union([z.string(), z.unknown()]).optional(),
	error: z.string().optional(),
	// OneDrive also sends client_info which we can ignore
	client_info: z.string().optional(),
});

const shouldRedirectForStorage = (oauthState: OauthState | null) => {
	if (!oauthState) {
		return false;
	}
	if (oauthState.reconnect) {
		return true;
	}
	const returnUrl = oauthState.returnUrl;
	// The SAME question `getStorageRedirectTarget` answers below, through the same
	// owner. Asking it twice with two `includes` chains is what made HubSpot's return
	// branch unreachable: the target knew about the integrations page, this gate
	// did not, so the redirect never fired and the consent code was dropped.
	if (connectReturnTarget(returnUrl) !== undefined) {
		return true;
	}
	// SSO states have formData.provider as an SsoProviderId — don't treat as storage
	if (typeof returnUrl === "string" && returnUrl.includes("/sso")) {
		return false;
	}
	return oauthState.formData !== undefined;
};

const getStorageRedirectTarget = (
	oauthState: OauthState,
	code: string,
	stateString: string,
) => {
	if (oauthState.reconnect) {
		return {
			to: "/dashboard/spaces/$spaceId",
			params: { spaceId: oauthState.reconnect.spaceId },
			search: {
				code,
				state: stateString,
			},
			replace: true,
		};
	}
	const returnUrl = oauthState.returnUrl;
	if (typeof returnUrl === "string" && returnUrl.startsWith("/")) {
		// A page that can COMPLETE the connect gets the code and state; anything else is
		// a plain navigation, because forwarding a code nobody spends leaves it in the
		// URL. One owner for that set, shared with the gate above.
		const target = connectReturnTarget(returnUrl);
		if (target !== undefined) {
			return {
				to: target,
				search: {
					code,
					state: stateString,
				},
				replace: true,
			};
		}
		return {
			to: returnUrl,
			replace: true,
		};
	}

	return {
		to: "/home",
		replace: true,
	};
};

export const Route = createFileRoute("/auth/oauth")({
	validateSearch: oauthSearchSchema,
	beforeLoad: ({ search, context }) => {
		const parsedSearch = oauthSearchSchema.parse(search);

		if (!parsedSearch.code || parsedSearch.state === undefined) {
			throw redirect({ to: "/auth/login", replace: true });
		}

		// A connector-connect state is signed now, so it is opaque to parseOauthState;
		// its routing payload lives inside the envelope. Unwrapping it here keeps the
		// storage-redirect logic below unchanged.
		const oauthState =
			parseOauthState(parsedSearch.state) ??
			peekSpaceConnectPayload(parsedSearch.state);

		if (oauthState && shouldRedirectForStorage(oauthState)) {
			const stateString = serializeOauthState(parsedSearch.state);
			if (stateString === null) {
				throw redirect({ to: "/home", replace: true });
			}
			throw redirect(
				getStorageRedirectTarget(oauthState, parsedSearch.code, stateString),
			);
		}

		if (parsedSearch.error) {
			return;
		}

		// Remaining case: the pre-login SSO flow, whose signed state carries the
		// provider. Prefetch is best-effort — the component re-runs the query and
		// surfaces any failure (including a rejected state) as a login error.
		const ssoState = peekSsoOauthState(parsedSearch.state);
		const flowSecret = readSsoFlowSecret();
		if (!ssoState || typeof parsedSearch.state !== "string" || !flowSecret) {
			return;
		}
		// Not awaited. The comment above already says the prefetch is best-effort
		// and that the component re-runs the query — but awaiting it meant the SSO
		// callback rendered nothing for a full round trip before the component was
		// even mounted. Fire it so the request is in flight, and let the component
		// render its own pending state against the same cache entry.
		context.queryClient
			.prefetchQuery(
				ssoOrganizationsQueryOptions(
					parsedSearch.code,
					parsedSearch.state,
					flowSecret,
					ssoState.provider,
				),
			)
			.catch((err: unknown) => {
				console.error("Failed to prefetch organizations:", err);
			});
	},
	component: () => {
		const ssoState = useOauthSso();
		return <OauthOrganizations {...ssoState} />;
	},
});
